import { useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Stack } from '@mui/material';
import { getRevenueAnalytics, getAgingDrillDown } from 'api/rest/analytics';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { useAuth } from 'contexts/ProtectedRoute';
import { Role } from 'data/constants/roles';

import { AgingDrillDownItem } from 'hooks/analytics';
import useAnalyticsData from '../../hooks/useAnalyticsData';
import useAnalyticsActions from '../../hooks/useAnalyticsActions';
import { AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import InlineMessageComposer from '../../components/InlineMessageComposer';
import RevenueMix from './RevenueMix';
import RevenueRecovery from './RevenueRecovery';
import ServicePriceHistogram from './ServicePriceHistogram';
import type { AnalyticsOutletContext } from '../../AnalyticsShell';

const RevenueView = () => {
    const { role } = useAuth();
    const isSuperSuperAdmin = role === Role.SUPER_SUPER_ADMIN;
    const { from, to, serviceProviderId } = useOutletContext<AnalyticsOutletContext>();

    // Provider override is only honored for SUPER_SUPER_ADMIN; for every other
    // admin role the backend scopes by the caller's own service provider.
    const providerScope = isSuperSuperAdmin ? serviceProviderId : undefined;

    const { data, loading, error, refetch } = useAnalyticsData(
        () => getRevenueAnalytics(from, to, providerScope),
        [from, to, providerScope]
    );
    const actions = useAnalyticsActions(refetch);

    const handleBucketDrillDown = useCallback(
        async (type: string, bucketIndex: number): Promise<AgingDrillDownItem[]> => {
            try {
                const res = await getAgingDrillDown(type, bucketIndex, from, to, providerScope);
                return res.data;
            } catch {
                return [];
            }
        },
        [from, to, providerScope]
    );

    if (loading) {
        return <AnalyticsLoading />;
    }

    if (error || !data) {
        return (
            <AnalyticsError message={error || 'Failed to load revenue data. Please try again.'} />
        );
    }

    return (
        <Stack spacing={2}>
            <RevenueMix programs={data.revenueByProgram} totalRevenue={data.totalRevenue} />
            <RevenueRecovery
                unconvertedOffers={data.unconvertedOffers}
                unpaidContracts={data.unpaidContracts}
                paymentVelocity={data.paymentVelocity}
                actions={actions}
                onBucketDrillDown={handleBucketDrillDown}
            />
            <ServicePriceHistogram from={from} to={to} serviceProviderId={serviceProviderId} />

            {actions.confirmDialog.open && (
                <ConfirmationDialog
                    title={actions.confirmDialog.title}
                    content={actions.confirmDialog.content}
                    loading={actions.confirmDialog.loading}
                    onClose={actions.closeConfirmDialog}
                    onConfirm={actions.confirmDialog.onConfirm}
                />
            )}

            <InlineMessageComposer
                open={actions.messageComposer.open}
                recipientName={actions.messageComposer.recipientName}
                template={actions.messageComposer.template}
                onClose={actions.closeMessageComposer}
                onSend={actions.sendMessage}
            />
        </Stack>
    );
};

export default RevenueView;
